import {BitrixVue} from 'ui.vue3'
import {Loader} from '../../../components/ui/Loader'
import {VerificationManager,} from '../../../components/ui/input/VerificationManager'

const CommentNode = {
    name: 'CommentNode',
    props: {
        comment: {
            type: Object,
            required: true,
        },
        depth: {
            type: Number,
            default: 0,
        },
    },
    emits: ['reply'],
    methods: {
        formatDate(dateString) {
            try {
                // Пробуем использовать BX.date.format если доступен
                if (typeof BX !== 'undefined' && BX.date && BX.date.format) {
                    return BX.date.format('d.m.Y H:i', new Date(dateString))
                } else {
                    // Fallback: используем нативный JavaScript
                    const date = new Date(dateString)
                    const day = date.getDate().toString().padStart(2, '0')
                    const month = (date.getMonth() + 1).toString().padStart(2, '0')
                    const year = date.getFullYear()
                    const hours = date.getHours().toString().padStart(2, '0')
                    const minutes = date.getMinutes().toString().padStart(2, '0')
                    return `${day}.${month}.${year} ${hours}:${minutes}`
                }
            } catch (error) {
                console.error('Error formatting date:', error)
                return dateString // Возвращаем оригинальную строку в случае ошибки
            }
        },
        handleReply() {
            this.$emit('reply', this.comment.ID)
        },
    },
    template: `
    <div class="comment-node" 
        :class="'comment-node--depth-' + depth"
        :data-comment-id="comment.ID"
    >
      <div class="comment-header">
        <span class="date">{{ formatDate(comment.UF_CREATED_AT) }}</span>
      </div>
      <div class="comment-text" v-html="comment.UF_COMMENT"></div>
      <div class="comment-footer">
        <span class="author">{{ comment.UF_NAME }}</span>
        <a 
        v-if="!comment.isAnswer" 
        @click.stop="handleReply" 
        class="reply-link"
      >
        Ответить
      </a>
      </div>
      <div v-if="comment.UF_ANSWER" class="comment-answer">
        <div class="comment-answer__header">Ответ автора:</div>
        <div class="comment-answer__content" v-html="comment.UF_ANSWER"></div>
      </div>
      
      <div class="comment-children" v-if="comment.children?.length">
        <CommentNode 
          v-for="child in comment.children"
          :key="child.ID"
          :comment="child"
          :depth="depth + 1"
          @reply="$emit('reply', $event)"
        />
      </div>
    </div>
  `,
}

export class Comments {
    #application = null
    #entityId = null
    #options = {}

    constructor(rootNode, entityId, options = {}) {
        this.rootNode = document.querySelector(rootNode)
        this.#entityId = entityId
        this.#options = options
    }

    start(): void {
        this.attachTemplate()
    }

    attachTemplate(): void {
        const context = this

        this.#application = BitrixVue.createApp({
            name: 'DzenCommentForm',
            components: {
                Loader,
                VerificationManager,
                CommentNode,
            },
            data() {
                if (typeof window.userVerified === 'undefined') {
                    window.userVerified = false;
                }
                if (typeof window.userId === 'undefined') {
                    window.userId = 0;
                }

                const isAuthenticated = (context.#options.userId || BX.message('USER_ID') || 0) > 0 || (window.userId || 0) > 0;

                return {
                    comments: [],
                    isLoading: false,
                    isSubmitting: false,
                    newComment: {
                        text: '',
                        parentId: 0,
                    },
                    verificationData: null,
                    activeReply: null,
                    showForm: false,
                    userId: context.#options.userId || BX.message('USER_ID') || 0,
                    userName: context.#options.userName || '',
                    requireNameInput: !isAuthenticated,
                    isVerified: BX.message('USER_ID') && parseInt(BX.message('USER_ID')) > 0,
                    isCodeVerified: BX.message('USER_ID') && parseInt(BX.message('USER_ID')) > 0,
                    verification: {
                        inProcess: false,
                        verified: false,
                        userData: null,
                    },
                    isAnswer: false,
                }
            },
            computed: {
                isAuthenticated() {
                    return (this.userId > 0) || ((window.userId || 0) > 0)
                },
                userVerified() {
                    return window.userVerified || false
                },
                showVerification() {
                    return !this.isAuthenticated && !this.userVerified
                },
                isSubmitDisabled() {
                    if (this.isSubmitting) return true
                    if (!this.newComment.text || this.newComment.text.trim().length < 5) return true

                    // Если пользователь авторизован (через битрикс или верификацию) - не проверяем имя
                    if (this.isAuthenticated || this.userVerified) return false

                    // Для неавторизованных проверяем имя
                    return !this.userName || !this.userName.trim()
                },
                hasComments() {
                    return this.comments.length > 0
                },
            },
            methods: {
                getReplyNoticeText(commentId) {
                    const comment = this.findCommentById(this.comments, commentId)
                    if (!comment) return 'Ответ на комментарий'

                    const date = this.formatDate(comment.UF_CREATED_AT)
                    return `Ответ на комментарий ${comment.UF_NAME} от ${date}`
                },
                formatDate(dateString) {
                    try {
                        if (typeof BX !== 'undefined' && BX.date && BX.date.format) {
                            return BX.date.format('d.m.Y H:i', new Date(dateString))
                        } else {
                            // Fallback implementation
                            const date = new Date(dateString)
                            const day = date.getDate().toString().padStart(2, '0')
                            const month = (date.getMonth() + 1).toString().padStart(2, '0')
                            const year = date.getFullYear()
                            const hours = date.getHours().toString().padStart(2, '0')
                            const minutes = date.getMinutes().toString().padStart(2, '0')
                            return `${day}.${month}.${year} ${hours}:${minutes}`
                        }
                    } catch (error) {
                        console.error('Error formatting date:', error)
                        return dateString
                    }
                },
                findCommentById(comments, id) {
                    for (const comment of comments) {
                        if (comment.ID === id) return comment
                        if (comment.children?.length) {
                            const found = this.findCommentById(comment.children, id)
                            if (found) return found
                        }
                    }
                    return null
                },
                toggleForm() {
                    this.showForm = !this.showForm
                    if (this.showForm && this.isAuthenticated) {
                        // Для авторизованных проверяем необходимость ввода имени
                        this.requireNameInput = !this.userName

                        this.$nextTick(() => {
                            const form = this.$el.querySelector('.comment-form')
                            if (form) form.scrollIntoView({behavior: 'smooth'})

                            // Фокусируемся на поле ввода (имени или комментария)
                            const field = this.requireNameInput
                                ? this.$el.querySelector('#comment-name')
                                : this.$el.querySelector('.comment-textarea')
                            if (field) field.focus()
                        })
                    }
                },
                async loadComments() {
                    this.isLoading = true
                    try {
                        const response = await BX.ajax.runComponentAction('dzen:comments',
                            'getComments', {
                                mode: 'ajax',
                                data: {entityId: context.#entityId},
                            })
                        this.comments = response.data?.comments || []
                        this.userName = response.data?.userName || this.userName || ''

                        if (response.data.newCommentId) {
                            this.$nextTick(() => {
                                const newCommentEl = this.$el.querySelector(
                                    `[data-comment-id="${response.data.newCommentId}"]`)
                                if (newCommentEl) {
                                    newCommentEl.classList.add('new-comment')
                                    newCommentEl.scrollIntoView(
                                        {behavior: 'smooth', block: 'nearest'})
                                }
                            })
                        }
                    } catch (error) {
                        console.error('Error loading comments:', error)
                    } finally {
                        this.isLoading = false
                    }
                },
                async submitComment() {

                    if (!this.validateComment()) return

                    this.isSubmitting = true
                    try {
                        const response = await BX.ajax.runComponentAction('dzen:comments',
                            'addComment', {
                                mode: 'ajax',
                                data: this.getSubmitData(),
                            })

                        if (response.status === 'success') {
                            BX.UI.Notification.Center.notify({
                                content: 'Комментарий успешно добавлен',
                                autoHideDelay: 3000,
                            })

                            await this.loadComments()
                            this.resetForm()
                            this.showForm = false
                            this.newComment.parentId = 0
                            this.activeReply = null
                        }
                    } catch (error) {
                        console.error('Error submitting comment:', error)
                        BX.UI.Notification.Center.notify({
                            content: 'Ошибка при отправке комментария: ' +
                                (error.message || 'Неизвестная ошибка'),
                            autoHideDelay: 5000,
                        })
                    } finally {
                        this.isSubmitting = false
                    }
                },
                getSubmitData() {
                    const data = {
                        entityId: context.#entityId,
                        userName: this.userName || '',
                        text: this.newComment.text,
                        parentId: this.newComment.parentId || 0,
                    }

                    // Добавляем имя пользователя только если не авторизованы через битрикс
                    if (!this.isAuthenticated) {
                        data.userName = this.userName
                    }

                    // Добавляем userData только если прошли верификацию и не авторизованы через битрикс
                    if (!this.isAuthenticated && this.userVerified && this.verification?.userData) {
                        data.userData = this.verification.userData
                    }

                    return data
                },
                buildTree(comments) {
                    const map = {}
                    const roots = []

                    comments.forEach(comment => {
                        map[comment.ID] = {...comment, children: []}
                    })

                    comments.forEach(comment => {
                        if (comment.UF_PARENT_COMMENT && map[comment.UF_PARENT_COMMENT]) {
                            map[comment.UF_PARENT_COMMENT].children.push(map[comment.ID])
                        } else {
                            roots.push(map[comment.ID])
                        }
                    })

                    return roots
                },
                startVerification() {
                    this.verification.inProcess = true
                },
                handleVerificationComplete({verified, phone, email, name}) {
                    if (verified) {
                        this.userName = name || this.userName
                        this.verification.verified = true
                        this.verification.userData = {phone, email}

                        // Автоматически фокусируемся на поле комментария после верификации
                        this.$nextTick(() => {
                            const textarea = this.$el.querySelector('.comment-textarea')
                            if (textarea) textarea.focus()
                        })
                    }
                },
                onVerificationError(error) {
                    this.verification.inProcess = false
                    console.error('Verification error:', error)
                    BX.UI.Notification.Center.notify({
                        content: 'Ошибка верификации: ' +
                            (error.message || 'Неизвестная ошибка'),
                        autoHideDelay: 5000,
                    })
                },
                flattenComments(comments) {
                    let flat = []
                    comments.forEach(comment => {
                        flat.push(comment)
                        if (comment.children.length) {
                            flat = [...flat, ...this.flattenComments(comment.children)]
                        }
                    })
                    return flat
                },
                validateComment() {
                    if (!this.newComment.text || this.newComment.text.trim().length < 5) {
                        BX.UI.Notification.Center.notify({
                            content: 'Комментарий слишком короткий (минимум 5 символов)',
                            autoHideDelay: 3000,
                        })
                        return false
                    }

                    if (this.isAuthenticated || this.userVerified) {
                        return true
                    }

                    if (!this.isAuthenticated) {
                        // Проверка верификации
                        if (!this.verification.verified) {
                            BX.UI.Notification.Center.notify({
                                content: 'Пожалуйста, завершите верификацию',
                                autoHideDelay: 3000,
                            })
                            return false
                        }
                    }

                    return true
                },
                resetForm() {
                    this.newComment.text = ''
                    this.newComment.parentId = 0
                    this.activeReply = null
                },
                replyTo(commentId) {
                    // Открываем форму, если она закрыта
                    if (!this.showForm) {
                        this.showForm = true
                    }

                    // Устанавливаем parentId для ответа
                    this.newComment.parentId = commentId
                    this.activeReply = commentId

                    // Прокрутка к форме (заменим BX.scrollTo на нативный способ)
                    this.$nextTick(() => {
                        const formElement = this.$el.querySelector('#comment-form')
                        if (formElement) {
                            formElement.scrollIntoView({
                                behavior: 'smooth',
                                block: 'nearest',
                            })
                        }
                    })
                },
                cancelReply() {
                    this.newComment.parentId = 0
                    this.activeReply = null
                },
                cancelComment() {
                    this.newComment.text = ''
                    this.newComment.parentId = 0
                    this.activeReply = null
                    this.showForm = false
                },
            },
            mounted() {
                this.loadComments()
            },
            template: `
<div class="dzen-comment-form">
  <Loader v-if="isLoading" />
  
  <div class="comment-list">
    <CommentNode 
      v-for="comment in comments"
      :key="comment.ID"
      :comment="comment"
      @reply="replyTo"
    />
  </div>
  
  <div v-if="!showForm" class="comment-action-link">
    <a 
      v-if="!hasComments"
      @click="toggleForm"
      class="comment-action-link__first"
    >
      Пока нет ни одного комментария, будьте первым
    </a>
    <a 
      v-else
      @click="toggleForm"
      class="comment-action-link__add"
    >
      Добавить комментарий
    </a>
  </div>
  
  <div 
    v-if="showForm"
    class="comment-form-wrapper"
  >
    <div class="comment-form" id="comment-form">
    
      <div v-if="isAuthenticated && requireNameInput && !userVerified" class="form-group name-field">
        <label for="comment-name">Ваше имя:</label>
        <input
          v-model="userName"
          type="text"
          id="comment-name"
          placeholder="Введите ваше имя"
          required
          class="comment-input"
          :disabled="isSubmitting"
        >
        <div v-if="isAuthenticated && requireNameInput" class="name-hint">
          Пожалуйста, укажите имя для комментариев
        </div>
      </div>
    
      <VerificationManager
        v-if="showVerification"
        :hide-email="true"
        :show-name-field="true"
        @verification-complete="handleVerificationComplete"
        @error="onVerificationError"
      />
      
      <div v-if="userVerified && !isAuthenticated" class="verification-success">
        ✓ Вы успешно авторизованы
      </div>
      
      <form @submit.prevent="submitComment">
        <Loader v-if="isSubmitting" overlay />
        
        <div v-if="activeReply" class="reply-notice">
          {{ getReplyNoticeText(activeReply) }}
          <button type="button" @click="cancelReply">×</button>
        </div>
        
        <div class="form-group">
          <label for="comment-text">Комментарий:</label>
          <textarea 
            v-model="newComment.text"
            :disabled="isSubmitting"
            placeholder="Введите ваш комментарий..."
            class="comment-textarea"
          ></textarea>
        </div>
        
        <div class="form-footer">
          <button 
            type="submit" 
            :disabled="isSubmitDisabled"
            class="button-orange"
          >
            <span v-if="!isSubmitting">Отправить</span>
            <span v-else>Отправка...</span>
          </button>
          <button 
              type="button"
              @click="cancelComment"
              class="button-orange button-orange--red"
            >
              Отмена
          </button>
        </div>
      </form>
    </div>
  </div>
</div>
`
        })

        this.#application.mount(this.rootNode)
    }

    destroy() {
        if (this.#application) {
            this.#application.unmount()
            this.#application = null
        }
    }
}
